import { CROPS, SEAFOOD, captureAvailability, dailyOrderStatus } from './economy.js';
import { JOURNAL_ENTRIES, normalizePlayerGoal, projectStatus } from './islandLife.js';
import { harvestReadiness, plotGuidance } from './playerGuidance.js';

const count = value => Number.isSafeInteger(value) && value > 0 ? value : 0;
const label = id => CROPS[id]?.label || SEAFOOD[id]?.label || ({ wood: '木材', shells: '贝壳' })[id] || id;
const potatoMeals = ['meal_bakedPotato_1', 'meal_bakedPotato_2', 'meal_bakedPotato_3'];

function cropStep(state, id, missing) {
  const spec = CROPS[id], plots = (state.plots || []).filter(plot => plot.crop?.id === id);
  const ripe = plots.find(plot => plot.crop.growthMinutes >= spec.minutes);
  if (ripe) {
    const readiness = harvestReadiness(state, ripe);
    if (!readiness.canHarvest) {
      const hint = plotGuidance(state, ripe);
      return { text: hint.detail, page: hint.page, plotId: ripe.id, itemId: id };
    }
    return { text: `收获成熟的${spec.label} · 还缺 ${missing} 个`, page: 'farm', plotId: ripe.id, itemId: id };
  }
  const dry = plots.find(plot => state.gameMinutes >= plot.crop.wateredUntil);
  if (dry) return { text: state.freshwater?.canteen > 0 ? `给${spec.label}浇水，让它继续生长`
    : `先到淡水收集场装水，再给${spec.label}浇水`, page: state.freshwater?.canteen > 0 ? 'farm' : 'water', plotId: dry.id, itemId: id };
  if (plots.length) {
    const remaining = Math.max(0, Math.ceil(Math.min(...plots.map(plot => spec.minutes - plot.crop.growthMinutes))));
    const paused = plots.some(plot => (plot.pauseUntil || 0) > state.gameMinutes);
    return { text: paused ? `${spec.label}受灾停长中 · 先采集或探索，稍后回来`
      : `${spec.label}还需 ${remaining} 游戏分钟 · 可以先去探索`, page: 'farm', plotId: plots[0].id, itemId: id };
  }
  if (count(state.inventory?.[`${id}Seed`])) return { text: (state.plots || []).some(plot => !plot.crop)
    ? `在空田种下${spec.label} · 还缺 ${missing} 个` : `田地已种满 · 收获后再种${spec.label}`, page: 'farm', itemId: id };
  if (count(state.storage?.[`${id}Seed`])) return { text: `先从仓库取回${spec.label}种子，再去播种`, page: 'inventory', itemId: `${id}Seed` };
  return { text: `到码头购买${spec.label}种子，再去空田播种`, page: 'shop', itemId: `${id}Seed` };
}

function fishStep(state, id, missing) {
  const spec = SEAFOOD[id], boats = { wreck: 0, wood: 1, iron: 2, speed: 3 };
  if (spec.rodLevel && (state.rodLevel || 1) < spec.rodLevel && !(state.soldLifetime > 0))
    return { text: '先捕鱼或收获，在码头出售一份货物，解锁鱼竿升级', page: 'activity' };
  if (spec.rodLevel && (state.rodLevel || 1) < spec.rodLevel)
    return { text: `到码头升级鱼竿至 Lv.${spec.rodLevel}，再寻找${spec.label}`, page: 'shop', itemId: `rod${spec.rodLevel}` };
  const nextBoat = spec.diveLevel && !state.diveLevel ? 'iron' : spec.boatTier;
  if (nextBoat && (boats[state.boatTier] || 0) < boats[nextBoat]) {
    if (state.boatDurability !== 100 || state.boatTier === 'wreck')
      return { text: '先到维修仓储修好船只，再升级远航装备', page: 'logistics' };
    if (nextBoat === 'speed' && state.boatTier === 'iron') {
      const storms = Math.max(state.stormCount || 0, state.storm?.settledId || 0);
      if (storms < 1) return { text: '快艇尚待解锁 · 经历风季后先升级码头', page: 'storm' };
      if (state.dockLevel < 2) return { text: '先升级码头至 Lv.2，再准备快艇', page: 'shop', itemId: 'dock2' };
      if (storms < 2) return { text: '快艇尚待解锁 · 安全度过第二场风季后再来', page: 'storm' };
      return { text: `升级快艇，再探索${spec.label}的水域`, page: 'shop', itemId: 'speedBoat' };
    }
    return { text: `先升级铁船，再探索${spec.label}的水域`, page: 'shop', itemId: 'ironBoat' };
  }
  if (spec.diveLevel && !state.diveLevel && !state.explored?.reef) {
    const reefStep = fishStep(state, 'reefFish', 1);
    return { ...reefStep, text: `先探索近岸珊瑚礁，解锁潜水装备 · ${reefStep.text}` };
  }
  if (spec.diveLevel && (state.diveLevel || 0) < spec.diveLevel) {
    const nextLevel = Math.min(spec.diveLevel, (state.diveLevel || 0) + 1);
    return { text: `准备 Lv.${nextLevel} 潜水装备，再寻找${spec.label}`, page: 'shop', itemId: `dive${nextLevel}` };
  }
  if (spec.supply && count(state.inventory?.diveSupply) < spec.supply)
    return { text: `备齐 ${spec.supply} 份潜水耗材，再下潜寻找${spec.label}`, page: 'shop', itemId: 'diveSupply' };
  if (spec.oxygen && (state.oxygen || 0) < spec.oxygen)
    return { text: `先补充潜水氧气，再下潜寻找${spec.label}`, page: 'inventory', itemId: 'diveSupply' };
  // Use the same read-only gate as capture settlement, after the actionable
  // equipment/supply steps above. A tracked goal stays selected while blocked.
  const availability = captureAvailability(state, id);
  if (!availability.ok) {
    if (availability.reason === 'capacity') return { text: `先整理背包，出售或存仓腾出 ${availability.requiredSpaces} 格，再寻找${spec.label}`,
      page: 'inventory', itemId: id, blockedReason: availability.reason };
    if (availability.reason === 'stamina') return { text: `捕获${spec.label}需 ${spec.stamina} 体力 · 稍歇几秒自然恢复，再继续探索`,
      page: 'activity', blockedReason: availability.reason };
    if (availability.reason === 'depleted') return { text: `${spec.label}暂时稀少 · 先探索其他内容，等鱼群自然恢复后再来`,
      page: 'voyage', blockedReason: availability.reason };
  }
  return { text: id === 'crab' ? `沿沙滩寻找沙蟹 · 还缺 ${missing} 只`
    : id === 'reefFish' ? `在近岸鱼群处垂钓 · 还缺 ${missing} 条${spec.label}`
      : `前往${spec.depth}水域${spec.tool === 'dive' ? '下潜' : '垂钓'} · 还缺 ${missing} 个${spec.label}`,
  page: 'activity', speciesId: id, itemId: id };
}

function materialStep(state, id, missing) {
  const stored = id === 'meal_bakedPotato_1' ? potatoMeals.reduce((sum, meal) => sum + count(state.storage?.[meal]), 0)
    : count(state.storage?.[id]);
  if (stored) return { text: `仓库已有${id === 'meal_bakedPotato_1' ? '炭烤地瓜' : label(id)} · 先取回背包`, page: 'inventory', itemId: id };
  if (id === 'wood' || id === 'shells') return { text: id === 'wood' ? `沿沙滩收集漂流木，或砍伐成熟椰树 · 还缺 ${missing} 木`
    : `沿沙滩寻找贝壳 · 还缺 ${missing} 枚`, page: 'activity', itemId: id };
  if (CROPS[id]) return cropStep(state, id, missing);
  if (SEAFOOD[id]) return fishStep(state, id, missing);
  if (id === 'meal_bakedPotato_1') {
    if (count(state.inventory?.sweetPotato)) return { text: '回小屋旁的海岛厨房，制作一份炭烤地瓜', page: 'kitchen', itemId: id };
    return materialStep(state, 'sweetPotato', 1);
  }
  return { text: '查看海岛手记，选择下一步探索', page: 'journal' };
}

// Pure derived guidance. Money, bags and time are changed only by real actions.
export function describePlayerGoal(state) {
  const goal = normalizePlayerGoal(state?.playerGoal);
  if (!goal) return null;
  if (goal.kind === 'wish') {
    const status = projectStatus(state, goal.id);
    if (status.completed) return { ...goal, title: status.title, done: true, text: '家园心愿已完成' };
    const total = status.requirements.reduce((sum, item) => sum + item.required, 0);
    const ready = status.requirements.reduce((sum, item) => sum + Math.min(item.required, item.contributed + item.held), 0);
    if (status.locked) return { ...goal, title: status.title, progress: `${ready}/${total}`, text: '先登船，首次登陆任意一座远岛', page: 'voyage' };
    const missing = status.requirements.filter(item => item.contributed + item.held < item.required);
    if (missing.length) {
      // A stocked warehouse is a quicker next step than another collection trip.
      const next = missing.find(item => item.itemId === 'meal_bakedPotato_1'
        ? potatoMeals.some(id => count(state.storage?.[id])) : count(state.storage?.[item.itemId])) || missing[0];
      return { ...goal, title: status.title, progress: `${ready}/${total}`,
        ...materialStep(state, next.itemId, next.required - next.contributed - next.held) };
    }
    return { ...goal, title: status.title, progress: `${ready}/${total}`, ready: true,
      text: state.voyage?.mode !== 'home' ? '材料已备齐 · 返航后到心愿板提交'
        : state.shelter?.inside ? '材料已备齐 · 先走出小屋，再到心愿板提交' : '材料已备齐 · 前往家园心愿板提交', page: 'journal' };
  }
  if (goal.kind === 'journal') {
    const entry = JOURNAL_ENTRIES.find(item => item.id === goal.id);
    const done = count(state.islandLife?.journal?.[goal.id]?.count) > 0;
    return { ...goal, title: `手记 · ${entry.label}`, progress: done ? '1/1' : '0/1', done,
      ...(done ? { text: '首次收获已记录' } : materialStep({ ...state, storage: {} }, goal.id, 1)) };
  }
  const status = dailyOrderStatus(state, goal.id);
  if (!status) return { ...goal, title: '海风来信', expired: true, text: '海风来信已在清晨更新' };
  if (status.completed) return { ...goal, title: status.title, done: true, text: '来信已交付' };
  const total = status.requirements.reduce((sum, item) => sum + item.quantity, 0);
  const ready = status.requirements.reduce((sum, item) => sum + Math.min(item.quantity, item.held), 0);
  const missing = status.requirements.filter(item => item.held < item.quantity);
  const next = missing.find(item => item.stored > 0) || missing[0];
  return { ...goal, title: status.title, progress: `${ready}/${total}`, ready: !next,
    ...(next ? materialStep(state, next.itemId, next.quantity - next.held)
      : { text: state.voyage?.mode !== 'home' ? '货物已备齐 · 返航后到心愿板交付'
        : state.shelter?.inside ? '货物已备齐 · 先走出小屋，再到心愿板交付' : '货物已备齐 · 前往家园心愿板交付', page: 'journal' }) };
}

export function selectPlayerGoal(state, kind, id) {
  const goal = normalizePlayerGoal({ kind, id });
  if (!goal) return { ok: false, message: '这份目标暂时无法追踪。' };
  if (state.playerGoal?.kind === goal.kind && state.playerGoal.id === goal.id) {
    state.playerGoal = null;
    return { ok: true, message: '已停止追踪 · 自由探索海岛' };
  }
  const hint = describePlayerGoal({ ...state, playerGoal: goal });
  if (!hint || hint.done || hint.expired) return { ok: false, message: hint?.text || '这份目标暂时无法追踪。' };
  state.playerGoal = goal;
  return { ok: true, message: `正在追踪${hint.title} · ${hint.text}` };
}

export function clearFinishedPlayerGoal(state) {
  const hint = describePlayerGoal(state);
  if (!hint?.done && !hint?.expired) return null;
  state.playerGoal = null;
  return hint;
}
